import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { nextVolumeTier, packPrice, PACKS, volumeTierFor } from "@/data/products";
import { TRAVEL_CARD } from "@/data/travelCard";
import { useCountry } from "@/context/CountryContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { market, currency } = useCountry();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tracksy-cart")) || [];
    } catch {
      return [];
    }
  });
  // Standalone Travel Card quantity. Kept separate from `items` so the
  // existing pack-based pricing/UI logic (volume tiers, Smart Repack) stays
  // untouched. Backend accepts this as a top-level `travel_cards` field.
  const [travelCards, setTravelCards] = useState(() => {
    try {
      return Number(JSON.parse(localStorage.getItem("tracksy-travelcards"))) || 0;
    } catch {
      return 0;
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Private browsing (iOS Safari especially) throws QuotaExceededError on
    // setItem. The React state is the source of truth; failing to persist
    // across reloads is a far better UX than a Clarity-captured JS error
    // that scares the shopper away.
    try { localStorage.setItem("tracksy-cart", JSON.stringify(items)); } catch {}
  }, [items]);

  useEffect(() => {
    try { localStorage.setItem("tracksy-travelcards", JSON.stringify(travelCards)); } catch {}
  }, [travelCards]);

  // Current-market unit price for a cart line — ignores the stale `price` field
  // on the item so market switches re-price everything instantly.
  const unitFor = (item) => {
    const pack = PACKS.find((p) => p.id === item.packId);
    return pack ? packPrice(pack, market) : item.price;
  };

  const addItem = (item) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.key === item.key);
      if (existing) {
        return prev.map((p) =>
          p.key === item.key ? { ...p, qty: p.qty + item.qty } : p
        );
      }
      return [...prev, item];
    });
    setIsOpen(true);
    // Meta Pixel: AddToCart — tag with the shopper's current currency.
    try {
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        const pack = PACKS.find((p) => p.id === item.packId);
        const price = pack ? packPrice(pack, market) : (item.price || 0);
        window.fbq("track", "AddToCart", {
          value: price * (item.qty || 1),
          currency,
          content_type: "product",
          contents: [{ id: item.key, quantity: item.qty || 1, item_price: price }],
          content_name: item.name,
        });
      }
    } catch { /* non-fatal */ }
  };

  const updateQty = (key, delta) => {
    setItems((prev) =>
      prev
        .map((p) => (p.key === key ? { ...p, qty: p.qty + delta } : p))
        .filter((p) => p.qty > 0)
    );
  };

  const clearCart = () => { setItems([]); setTravelCards(0); };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
  };

  // Travel Card (standalone, no bundle logic). Opens the drawer so the
  // shopper immediately sees the add — matches `addItem` behaviour.
  const addTravelCards = (qty = 1) => {
    const n = Math.max(1, Number(qty) || 1);
    setTravelCards((prev) => Math.min(99, prev + n));
    setIsOpen(true);
    // Meta Pixel AddToCart for Travel Card.
    try {
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        const price = TRAVEL_CARD.priceByCurrency[currency] || TRAVEL_CARD.priceByCurrency.AUD;
        window.fbq("track", "AddToCart", {
          value: price * n,
          currency,
          content_type: "product",
          contents: [{ id: TRAVEL_CARD.id, quantity: n, item_price: price }],
          content_name: TRAVEL_CARD.name,
        });
      }
    } catch { /* non-fatal */ }
  };
  const updateTravelCards = (delta) =>
    setTravelCards((prev) => Math.max(0, Math.min(99, prev + delta)));
  const removeTravelCards = () => setTravelCards(0);

  const value = useMemo(() => {
    // Always price against the current market, not the snapshot taken at add-time.
    const pricedItems = items.map((i) => ({ ...i, price: unitFor(i) }));
    const packSubtotal = pricedItems.reduce((s, i) => s + i.price * i.qty, 0);
    const trackerCount = pricedItems.reduce((s, i) => s + i.packId * i.qty, 0);
    const tier = volumeTierFor(trackerCount);
    const nextTier = nextVolumeTier(trackerCount);
    const discount = tier ? Math.round(packSubtotal * tier.discount) : 0;
    // Travel Card: standalone paid + free bundle bonus (1 per 4-Pack, 1 per 8-Pack).
    const travelCardUnit = TRAVEL_CARD.priceByCurrency[currency] || TRAVEL_CARD.priceByCurrency.AUD;
    const travelCardSubtotal = travelCards * travelCardUnit;
    const freeTravelCards = pricedItems.reduce(
      (s, i) => s + (i.packId === 4 ? 1 : i.packId === 8 ? 1 : 0) * i.qty, 0
    );
    const subtotal = packSubtotal + travelCardSubtotal;
    const count = pricedItems.reduce((s, i) => s + i.qty, 0) + travelCards;
    return {
      items: pricedItems,
      subtotal,
      count,
      trackerCount,
      tier,
      nextTier,
      discount,
      total: subtotal - discount,
      currency,
      isOpen,
      setIsOpen,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      // Travel Card API
      travelCards,
      travelCardUnit,
      travelCardSubtotal,
      freeTravelCards,
      addTravelCards,
      updateTravelCards,
      removeTravelCards,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isOpen, market, currency, travelCards]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
